import {
  Checkbox,
  Collapse,
  MenuItem,
  Stack,
  Typography,
  styled,
} from "@mui/material"
import { ChevronDown } from "react-feather"
import { type TreeNode, getNodeState } from "../treeSelectMenu.utils"
import { MenuItemContainer } from "./MenuItemContainer"
import { TreeLeafItem } from "./TreeLeafItem"

export interface TreeParentItemProps<T extends string = string> {
  node: TreeNode<T>
  value: T[]
  isOpen: boolean
  onToggleExpand: (code: T) => void
  onSelect: (node: TreeNode<T>) => void
}

export const TreeParentItem = <T extends string = string>({
  node,
  value,
  isOpen,
  onToggleExpand,
  onSelect,
}: TreeParentItemProps<T>) => {
  const state = getNodeState(node, value)
  return (
    <MenuItemContainer>
      <MenuItem
        data-testid={`tree-expand-${node.code}`}
        onClick={() => onToggleExpand(node.code)}
        aria-expanded={isOpen}
        aria-checked={
          state === "checked" ? true : state === "indeterminate" ? "mixed" : false
        }
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ width: "100%" }}>
          <Checkbox
            data-testid={`tree-checkbox-${node.code}`}
            checked={state === "checked"}
            indeterminate={state === "indeterminate"}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onSelect(node)}
          />
          <Typography sx={{ flex: 1, fontWeight: 500 }}>{node.label}</Typography>
          <Chevron open={isOpen} />
        </Stack>
      </MenuItem>
      <Collapse in={isOpen}>
        {node.children?.map((child) => (
          <TreeLeafItem
            key={child.code}
            node={child}
            value={value}
            indent={1}
            onSelect={onSelect}
          />
        ))}
      </Collapse>
    </MenuItemContainer>
  )
}

const Chevron = styled(ChevronDown, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open: boolean }>(({ open }) => ({
  transition: "transform 0.2s",
  ...(open && { transform: "rotate(180deg)" }),
}))
